/**
 * LeaveRequestPage — Szabadság kérelem oldal
 * Saját kérelmek listája + LeaveRequestForm
 */

import { useTranslation } from '@kgc/i18n';
import { Badge, Card, CardContent, CardHeader, CardTitle } from '@kgc/ui';
import type { LeaveRequestStatus } from '../api/hr-api';
import { LeaveRequestForm } from '../components/LeaveRequestForm';
import { useLeaveRequests } from '../hooks/useLeaveRequests';

const STATUS_BADGE_VARIANT: Record<
  LeaveRequestStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  APPROVED: 'default',
  PENDING: 'secondary',
  REJECTED: 'destructive',
  CANCELLED: 'outline',
};

export function LeaveRequestPage() {
  const { t } = useTranslation();
  const { data: requests = [], isLoading } = useLeaveRequests();

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold">{t('hr.leave.title')}</h1>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Új kérelem form */}
        <Card>
          <CardHeader>
            <CardTitle>{t('hr.leave.request')}</CardTitle>
          </CardHeader>
          <CardContent>
            <LeaveRequestForm />
          </CardContent>
        </Card>

        {/* Saját kérelmek */}
        <Card>
          <CardHeader>
            <CardTitle>{t('hr.leave.myRequests')}</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading && (
              <p className="text-center text-muted-foreground py-8">{t('common.loading')}</p>
            )}
            {!isLoading && requests.length === 0 && (
              <p className="text-center text-muted-foreground py-8">{t('hr.leave.noRequests')}</p>
            )}
            <div className="space-y-3">
              {requests.map(req => (
                <div
                  key={req.id}
                  className="flex items-start justify-between rounded-lg border p-3"
                >
                  <div className="space-y-1">
                    <p className="text-sm font-medium">
                      {new Date(req.startDate).toLocaleDateString('hu-HU')} –{' '}
                      {new Date(req.endDate).toLocaleDateString('hu-HU')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {req.workingDays} {t('hr.leave.workingDays')}
                      {req.reason ? ` · ${req.reason}` : ''}
                    </p>
                  </div>
                  <Badge variant={STATUS_BADGE_VARIANT[req.status]}>
                    {t(`hr.leave.${req.status.toLowerCase()}`)}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
